import { cleanText, computeCorrectScore, getText } from "./helper";
import {
  computePartCount,
  computePartDepth,
  scoreWeights,
  tagLengthBoundaries,
  tagLengthScore,
} from "./planHelper";

// CHECKS
// Heading structure
const checkHeadingStructure = (hTags) => {
  if (hTags.length == 0) {
    return -1;
  }
  const headingLevels = hTags.map((tag) =>
    parseInt(tag.tag.replace("h", ""), 10)
  );

  for (let i = 0; i < headingLevels.length - 1; i++) {
    if (headingLevels[i] - headingLevels[i + 1] < -1) {
      return 0;
    }
  }

  return 100;
};

// Part count
const checkPartCount = (hTags, h2, h3, h4, formikProps) => {
  if (!getText(formikProps.values.outline)) {
    return { score: -1, status: "too_few" };
  }
  return computePartCount(
    hTags,
    h2,
    h3,
    h4,
    formikProps.values.nb_words,
    formikProps.values.outline
  );
};

// Similarity
const checkSimilarity = (tags) => {
  if (tags.length == 0) {
    return -1;
  }
  const seen = new Set();

  for (const { tag, text } of tags) {
    const cleanedText = text;
    const uniqueKey = `${tag}-${cleanedText}`;

    if (seen.has(uniqueKey)) {
      return 0;
    }

    seen.add(uniqueKey);
  }

  return 100;
};

// Part depth
const countDirectSubparts = (tags) => {
  const tagHierarchy = {
    h1: 1,
    h2: 2,
    h3: 3,
    h4: 4,
    h5: 5,
    h6: 6,
  };
  const result = tags.map((tag) => ({
    tag: tag.tag,
    text: tag.text,
    directSubparts: 0,
  }));

  for (let i = 0; i < tags.length; i++) {
    const currentTag = tags[i];
    const currentLevel = tagHierarchy[currentTag.tag];
    let subpartCount = 0;

    for (let j = i + 1; j < tags.length; j++) {
      const nextTag = tags[j];
      const nextLevel = tagHierarchy[nextTag.tag];

      if (nextLevel === currentLevel + 1) {
        subpartCount++;
      } else if (nextLevel <= currentLevel) {
        break;
      }
    }

    result[i].directSubparts = subpartCount;
  }

  return result;
};

const checkPartDepth = (tags) => {
  if (tags.length === 0) {
    return { score: -1, part_count_too_short: 0, part_count_too_long: 0 };
  }
  const res = countDirectSubparts(tags);

  return computePartDepth(res);
};

// Optimal length
const checkOptimalLength = (tags) => {
  if (tags.length === 0)
    return { percentage: -1, score: -1, tooShort: 0, tooLong: 0 };

  let tooShort = 0,
    tooLong = 0;

  tags.forEach(({ tag, text }) => {
    const length = text.length;

    for (const [key, { min, max }] of Object.entries(tagLengthBoundaries)) {
      if (tag.includes(key)) {
        tooShort += length < min ? 1 : 0;
        tooLong += length > max ? 1 : 0;
        break;
      }
    }
  });

  const percentage = ((tooShort + tooLong) / tags.length) * 100;

  return {
    percentage: parseFloat(percentage.toFixed(2)),
    score: tagLengthScore(percentage),
    tooShort,
    tooLong,
  };
};

// START ANALYSIS
export const startAnalysis = (formikProps) => {
  const d = formikProps.values.outline;
  const matches = [...d.matchAll(/<h(\d)>(.*?)<\/h\1>/g)].map((match) => ({
    tag: `h${match[1]}`,
    text: cleanText(match[2]),
  }));

  const tagCounts = {
    h1: 0,
    h2: 0,
    h3: 0,
    h4: 0,
    h5: 0,
    h6: 0,
  };

  matches.forEach(({ tag }) => {
    tagCounts[tag] += 1;
  });
  const headingStructure = checkHeadingStructure(matches);
  const similarity = checkSimilarity(matches);
  const partCount = checkPartCount(
    matches,
    tagCounts["h2"],
    tagCounts["h3"],
    tagCounts["h4"],
    formikProps
  );
  const partDepth = checkPartDepth(matches);
  const optimalLength = checkOptimalLength(matches);

  // const totalScore = computeScore([headingStructure, similarity, partCount.score, partDepth.score, optimalLength.score], 0.2);
  const totalScore = computeCorrectScore(
    [
      headingStructure,
      similarity,
      partCount.score,
      partDepth.score,
      optimalLength.score,
    ],
    scoreWeights
  );

  return {
    hTags: matches,
    h1_count: tagCounts["h1"],
    h2_count: tagCounts["h2"],
    h3_count: tagCounts["h3"],
    h4_count: tagCounts["h4"],
    h5_count: tagCounts["h5"],
    h6_count: tagCounts["h6"],
    nb_words: formikProps.values.nb_words,
    checkHeadingStructure: headingStructure,
    checkSimilarity: similarity,
    checkPartCount: partCount,
    checkPartDepth: partDepth,
    checkOptimalLength: optimalLength,
    total_score: Math.round(totalScore),
  };
};
